import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Layers, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const URL_ = `${API}/api/admin/llm-dedup/canonical-detect/shadow-report`;

const exportJson = (r) => {
  const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `canonical-detect-shadow-${(r.finished_at || '').slice(0, 19).replace(/:/g, '')}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const GroupRow = ({ g, i }) => (
  <tr className="border-t border-gray-200 dark:border-gray-700 align-top" data-testid={`canon-detect-group-${i}`}>
    <td className="p-2 font-medium">{g.members[0].after_artist} — {g.members[0].after_title}</td>
    <td className="p-2">
      {g.members.map((m) => (
        <div key={m.canonical_key} className="text-gray-500">{m.before_artist} — {m.before_title} <span className="text-[10px]">({m.plays})</span></div>
      ))}
    </td>
    <td className="p-2 text-center">{g.docs}</td>
    <td className="p-2 text-center">{g.plays}</td>
  </tr>
);

export const CanonicalDetectShadowPanel = ({ authConfig }) => {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const mounted = useRef(true);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const d = (await axios.get(URL_, authConfig)).data;
      if (!mounted.current) return;
      if (d.status === 'running') { setBusy(true); timer.current = setTimeout(poll, 3000); return; }
      setBusy(false);
      if (d.status === 'done') setRep(d);
      if (d.status === 'error') setErr(d.error);
    } catch (e) { if (mounted.current) { setBusy(false); setErr(e.response?.data?.detail || e.message); } }
  }, [authConfig]);

  useEffect(() => {
    mounted.current = true;
    poll();
    return () => { mounted.current = false; clearTimeout(timer.current); };
  }, [poll]);

  const start = async () => {
    setBusy(true); setErr('');
    try { await axios.post(URL_, {}, authConfig); clearTimeout(timer.current); timer.current = setTimeout(poll, 1500); }
    catch (e) { setBusy(false); setErr(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800 space-y-3" data-testid="canon-detect-panel">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="flex items-center gap-1 font-semibold"><Layers className="w-4 h-4 text-teal-600" />Ανίχνευση με κανονικοποιημένα ονόματα (Shadow):</span>
        <button onClick={start} disabled={busy} data-testid="canon-detect-run-btn"
          className="px-3 py-1 rounded bg-teal-100 hover:bg-teal-200 text-teal-900 dark:bg-teal-900/40 dark:text-teal-200 disabled:opacity-50 transition-colors">
          {busy ? 'Υπολογισμός...' : 'Αναφορά «ποια θα ενώνονταν»'}
        </button>
        {rep && !busy && (
          <button onClick={() => exportJson(rep)} data-testid="canon-detect-export-btn"
            className="px-3 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 flex items-center gap-1 transition-colors">
            <Download className="w-4 h-4" />Εξαγωγή JSON
          </button>
        )}
        {rep?.finished_at && !busy && (
          <span className="text-gray-500" data-testid="canon-detect-meta">τελευταία: {new Date(rep.finished_at).toLocaleString('el-GR')} ({rep.seconds}s)</span>
        )}
        {err && <span className="text-red-600" data-testid="canon-detect-error">{String(err)}</span>}
      </div>
      {rep?.summary && (
        <div className="space-y-2" data-testid="canon-detect-report">
          <div className="flex flex-wrap gap-3 text-xs" data-testid="canon-detect-summary">
            {Object.entries(rep.summary).map(([k, v]) => (
              <span key={k} className="px-2 py-1 rounded bg-white dark:bg-gray-900"><b>{k}:</b> {typeof v === 'object' ? JSON.stringify(v) : v}</span>
            ))}
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800">
                <tr><th className="p-2 text-left">Κανονικοποιημένο</th><th className="p-2 text-left">Αρχικές γραφές (plays)</th><th className="p-2">Εγγραφές</th><th className="p-2">Plays</th></tr>
              </thead>
              <tbody>{rep.groups.map((g, i) => <GroupRow key={g.key} g={g} i={i} />)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
